/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { IApiQueryResults, IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import * as Core from 'app/core/models/essette-api';

export interface IUserNotificationSearchFilters {
    dateStart?: string | null;
    dateEnd?: string | null;
    includeRead?: boolean | null;
}

@autoinject
export class AccountService {

    constructor(public readonly api: Api, private readonly logger: AppLogger) {
        this.logger.source = this;
    }

    ////****************** Queries **************************************************////

    public async loadAccount(userId: string, includes: string[] = []): Promise<IApiQueryResults<Core.Account>> {
        // TODO: remove userId once filter is in place
        const query = this.api
            .query('account')
            .strategy('security')
            .include(...includes)
            .filterString('id', userId)
            .applyStandardLoadOptions({ top: 1 });

        return await this.api.get(query);
    }

    public async loadAccountContext(userContextId: string, includes: string[] = []): Promise<IApiQueryResults<Core.AccountContext>> {
        // TODO: remove userId once filter is in place
        const query = this.api
            .query('account/context')
            .strategy('security')
            .include(...includes)
            .filterString('id', userContextId)
            .applyStandardLoadOptions({ top: 1 });

        return await this.api.get(query);
    }

    public async loadUnreadNotifications(count: number = 100): Promise<IApiQueryResults<Core.UserNotification>> {
        const query = this.api
            .query('account/notifications')
            .strategy('polling')
            .include('type', 'metadata.triggered.byUser')
            .filterBoolean('isRead', false)
            .filterBoolean('isOptedOut', false)
            .applyStandardLoadOptions({ top: count })
            .excludeTotalRecordsCount();

        return await this.api.get(query);
    }

    public async loadAccountNotifications(options?: Partial<IQueryLoadOptions>, includes: string[] = [], filters: IUserNotificationSearchFilters = {}): Promise<IApiQueryResults<Core.UserNotification>> {
        const query = this.api
            .query('account/notifications')
            .applyStandardLoadOptions(options)
            .filterBoolean('isOptedOut', false)
            .include(...includes);

        if(filters.includeRead) {
            query.filterOr((or) => {
                or.filterBoolean('isRead', true);
                or.filterBoolean('isRead', false);
            });
        } else {
            query.filterBoolean('isRead', false)
        }

        if(filters.dateStart) {
            query.filterDateTimeBetweenDates(
                'metadata.triggered.timestamp',
                filters.dateStart,
                filters.dateEnd || null);
        }

        return this.api.get(query);
    }

    public async loadHomePages(includes: string[] = []): Promise<IApiQueryResults<Core.AccountHomePage>> {
        const query = this.api
            .query('account/pages/home')
            .include(...includes)
            .applyStandardLoadOptions({ all: true });

        return await this.api.get(query);
    }

    public async loadHomePageMyAccount(): Promise<IApiQueryResults<Core.AccountHomePage>> {
        const query = this.api
            .query('account/pages/home')
            .filterString('code', 'core.my-account')
            .applyStandardLoadOptions({ all: true });

        return await this.api.get(query);
    }

    public async markNotificationRead(notificationId: string, isRead: boolean = true): Promise<IApiActionResults> {
        const action = this.api
            .action('read', `account/notifications/${notificationId}`, { isRead });

        return await this.api.post(action);
    }

    public async markNotificationsRead(userId: string, notifications: Core.UserNotification[]): Promise<IApiActionResults> {
        const action = this.api
            .action('read', `account/${userId}`, { notifications });

        return await this.api.post(action);
    }

    public async loadPages(): Promise<IApiQueryResults<Core.NavigationPage>> {
        const query = this.api
            .query('account/pages')
            .strategy('security')
            .applyStandardLoadOptions({ all: true });

        return await this.api.get(query);
    }

    public async loadPermissions(): Promise<IApiQueryResults<Core.UserPermission>> {
        const query = this.api
            .query('account/permissions')
            .strategy('security')
            .applyStandardLoadOptions({ all: true });

        return await this.api.get(query);
    }

    public async updateAccountContext($accountContext: Core.AccountContext): Promise<IApiActionResults> {
        const action = this.api.action('update', `account/context/${$accountContext.id}`, $accountContext);

        return await this.api.post(action);
    }

    public async updateNotificationPreferences(userId: string, notificationTypes: Core.UserNotificationType[]): Promise<IApiActionResults> {
        const action = this.api.action('setDetails', `account/${userId}`, { notificationTypes });

        return await this.api.post(action);
    }

}
